/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import prettyBytes from 'pretty-bytes';
import { ChangeEvent, memo, useEffect, useState } from 'react';

import { OutlinedInput, OutlinedInputProps } from '@mui/material';

import { UnitPicker } from './components/UnitPicker';
import { CapacityUnits, UnitsToBytesMultiplier } from './components/UnitPicker.types';
import { findIndex, getUnit } from './utils';

type HUBytesInputProps = Pick<OutlinedInputProps, 'name' | 'label'> & {
  bytes: number;
  onChange: (bytes: string) => void;
  minBytes: number;
  maxBytes: number;
};

export const HUBytesInput = memo<HUBytesInputProps>(props => {
  const { bytes, onChange, minBytes, maxBytes, ...inputProps } = props;

  const [capacity, setCapacity] = useState<string>('');
  const [unit, setUnit] = useState<CapacityUnits>(CapacityUnits.GB);
  const [unitOptions, setUnitOptions] = useState<CapacityUnits[]>([]);

  useEffect(() => {
    const [capacity, unit] = prettyBytes(bytes).split(' ');
    setCapacity(capacity);
    setUnit(unit as CapacityUnits);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unitOptions = Object.values(CapacityUnits);

    const indexFrom = findIndex(unitOptions, getUnit(minBytes) as CapacityUnits) ?? 0;
    const indexTo = findIndex(unitOptions, getUnit(maxBytes) as CapacityUnits) ?? unitOptions.length;

    setUnitOptions(unitOptions.filter((_, idx) => idx >= indexFrom && idx <= indexTo));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCapacityChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.persist();
    const { value } = e.target;
    setCapacity(value);
    onChange(value ? String(Number(value) * UnitsToBytesMultiplier[unit]) : value);
  };

  const handleUnitChange = (value: CapacityUnits) => {
    setUnit(value);
    onChange(capacity ? String(Number(capacity) * UnitsToBytesMultiplier[value]) : capacity);
  };

  return (
    <OutlinedInput
      {...inputProps}
      value={capacity}
      onChange={handleCapacityChange}
      type="number"
      inputProps={{
        min: 1,
      }}
      fullWidth
      endAdornment={<UnitPicker unit={unit} onUnitChange={handleUnitChange} unitOptions={unitOptions} />}
    />
  );
});
